import { Pill } from '@design-system';
import classNames from 'classnames';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { AssetStoryblok } from '../../types/generated-storyblok';
import { Image } from '../Image';

type Props = {
  thumbnail?: AssetStoryblok;
  title: string;
  subtitle?: string;
  description?: string;
  releaseStatus?: 'upcoming' | 'register' | 'live';
  isDarkMode?: boolean;
  onThumbnailClick?: () => void;
  resolvedArtists?: {
    content: {
      name: string;
      thumbnail: AssetStoryblok;
    };
    full_slug: string;
    slug: string;
  }[];
};

const ThumbnailImage: React.FC<{ image: AssetStoryblok }> = ({ image }) => {
  return (
    <Image
      alt={image.alt || ''}
      className="h-full"
      eagerLoading
      focus={image.focus}
      hoverEffect
      shape="rounded"
      size={{
        sm: { ratio: '1/1', span: 4 },
        md: { ratio: '1/1', span: 4 },
        lg: { ratio: '1/1', span: 4 },
      }}
      src={image.filename}
      width={96}
    />
  );
};

export const ArticleHeadingReleases: React.FC<Props> = ({
  description,
  isDarkMode = false,
  onThumbnailClick,
  releaseStatus,
  resolvedArtists = [],
  subtitle,
  thumbnail,
  title = 'Avant Arte',
}) => {
  const { t } = useTranslation('releases');

  const hasThumbnailImages =
    resolvedArtists.length > 0 && thumbnail && thumbnail.filename !== null;

  const getReleaseLabel = (status: 'upcoming' | 'register' | 'live') => {
    switch (status) {
      case 'live':
        return t('status.live');
      case 'register':
        return t('status.register');
      default:
        return t('status.upcoming');
    }
  };

  return (
    <div className="flex w-full flex-col items-center justify-center gap-y-6 py-9 text-center md:py-12">
      {hasThumbnailImages ? (
        <div className="flex flex-wrap items-center justify-center gap-3">
          {resolvedArtists.length > 0 ? (
            resolvedArtists.map((artist) => (
              <div
                key={artist.slug}
                className="relative h-16 w-16 shrink-0 sm:h-24 sm:w-24"
              >
                <Link
                  className="group"
                  href={`/${artist.full_slug}`}
                  onClick={onThumbnailClick}
                  prefetch={false}
                  target="_blank"
                >
                  <ThumbnailImage image={artist.content.thumbnail} />
                </Link>
              </div>
            ))
          ) : (
            <div className="relative h-16 w-16 shrink-0 sm:h-24 sm:w-24">
              <ThumbnailImage image={thumbnail} />
            </div>
          )}
        </div>
      ) : null}

      <div className="flex flex-col">
        <h1
          className={classNames(
            'w-full text-balance text-lg font-bold md:text-xl lg:text-[60px] lg:leading-none xl:text-[80px]',
            isDarkMode ? 'text-white' : 'text-neutral-6',
          )}
        >
          {title}
        </h1>

        {subtitle ? (
          <h2
            className={classNames(
              'w-full text-balance text-lg md:text-xl lg:text-[60px] lg:leading-none xl:text-[80px]',
              isDarkMode ? 'text-white' : 'text-neutral-6',
            )}
          >
            {subtitle}
          </h2>
        ) : null}
      </div>

      {description ? (
        <p
          className={classNames(
            'mx-auto w-full max-w-lg text-balance text-sm md:text-md',
            isDarkMode ? 'text-white' : 'text-neutral-6',
          )}
        >
          {description}
        </p>
      ) : null}

      {releaseStatus ? (
        <Pill
          isActive={releaseStatus === 'live' || releaseStatus === 'register'}
          label={getReleaseLabel(releaseStatus)}
        />
      ) : null}
    </div>
  );
};
